import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import AIModelSelector from '../../pages/cerokm/components/AIModelSelector';
import AIResponseStats from './AIResponseStats';
import { useMounted } from './AskAI';
import BadgeId from '../common/BadgeId';
import TextDiff from '../test-sets/TextDiff';
import TextAreaWithHistory from '../common/editors/TextAreaWithHistory';
import { applyMongoUpdate, diffKeys } from './mongoUpdateUtils';

const STREAMING_SERVICE = 'services/llm/prompt-streaming';
const FALLBACK_SERVICE = 'services/openai/prompt';
const isStreamingModel = (m) => m && m.startsWith('gemini');

const PROMPT_HEADER = `You are a JSON data editor for a MongoDB collection.
You will receive (1) an array of source documents in JSON, then (2) a free-text instruction from the user describing modifications to apply.

Your task: produce a JSON array of MongoDB-compatible updates, ONE entry per document that needs to change. Documents that need no change MUST be omitted from the array.`;

const PROMPT_FORMAT = `OUTPUT FORMAT — return ONLY this JSON (no markdown fences, no commentary):
[
  {
    "_id": "<the document _id, copied verbatim from the input>",
    "update": {
      "$set":      { "field": value, "nested.path": value },
      "$unset":    { "field": true },
      "$addToSet": { "arrayField": { "$each": [item, ...] } },
      "$pull":     { "arrayField": { "$in": [item, ...] } },
      "$push":     { "arrayField": { "$each": [item, ...] } }
    }
  }
]

RULES:
- Include ONLY the operators you actually need; omit empty operators.
- Never include both $set and $unset for the same field in the same update.
- Never modify the _id field.
- For arrays, prefer $addToSet/$pull over $set unless the user asks for a full replacement.
- Be conservative: if the user instruction is ambiguous about a doc, leave it out.`;

// Fields the DB / admin manage themselves: the LLM must never set them on a new doc, and we strip
// them defensively from whatever it returns.
const MANAGED_FIELDS = ['_id', '__v', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', '__new', '__unsaved', '__deleted'];

const ADD_PROMPT_HEADER = `You are a JSON data author for a MongoDB collection.
You will receive (1) an array of EXISTING documents in JSON that show the schema and conventions of the collection, then (2) a free-text instruction from the user describing which NEW documents to create.

Your task: produce a JSON array of NEW documents to insert, conforming exactly to the structure, field names, types, value formats and conventions of the existing documents.`;

const ADD_PROMPT_FORMAT = `OUTPUT FORMAT — return ONLY a JSON array of new documents (no markdown fences, no commentary):
[
  { "field": value, ... },
  ...
]

RULES:
- NEVER set ${MANAGED_FIELDS.join(', ')} or any field starting with "__": those are assigned by the database and the admin.
- Every new document must be COMPLETE: include all the fields a document of this collection normally has (use the examples as the reference, including fields shown only in the full example), unless the examples show a field is optional and it does not apply.
- Mirror the existing documents' conventions exactly: same field names, nesting, casing, id formats, locale codes, enum values, etc.
- Do NOT repeat documents that already exist in the examples unless the user explicitly asks for copies.
- Be conservative: if the user instruction is ambiguous, create fewer documents rather than guessing.`;

// Field paths checked (in order) to identify a doc when the caller gives no labelFields.
// Ordered by how identifying / common they are across the CRUD collections that open this
// modal. Supports dotted paths (via _.get) since modelId is often nested under a wrapper.
// Only the first few that exist on a doc are shown.
const IDENTITY_FIELD_HINTS = [
  'modelId', 'idModelo', 'context.modelId',
  'genId', 'gen', 'make', 'marca', 'modelo',
];

function valueToShortString(v) {
  if (v == null) return '';
  if (_.isObject(v)) { try { return JSON.stringify(v); } catch (e) { return String(v); } }
  return String(v);
}

// Fields shown under the id badge to identify a doc: the caller's labelFields if given
// (all of them, in order), otherwise whichever IDENTITY_FIELD_HINTS exist (capped at 3).
function pickIdentityFields(doc, labelFields) {
  const candidates = labelFields?.length ? labelFields : IDENTITY_FIELD_HINTS;
  return candidates
    .map(k => ({ key: k, value: valueToShortString(_.get(doc, k)) }))
    .filter(f => f.value !== '')
    .slice(0, labelFields?.length || 3);
}

function DocIdentity({ doc, labelFields }) {
  const fields = pickIdentityFields(doc, labelFields);
  return <div style={{ maxWidth: '170px' }}>
    <BadgeId id={doc._id} />
    {fields.map(f => <div key={f.key}
      title={`${f.key}: ${f.value}`}
      style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
      className={'small'}>
      {f.key}&nbsp;<code className={'monospace text-secondary select-text'}>{f.value}</code>
    </div>)}
  </div>;
}

function pickDocFields(docs, selectedFields) {
  if (selectedFields == null) return docs;
  return docs.map(d => _.pick(d, ['_id', ...selectedFields]));
}

function buildEditPrompt(docs, userInstruction, selectedFields) {
  return [
    PROMPT_HEADER,
    'DOCUMENTS:',
    JSON.stringify(pickDocFields(docs, selectedFields), null, 2),
    'USER INSTRUCTION:',
    userInstruction,
    PROMPT_FORMAT,
  ].join('\n\n');
}

// Add mode: the selected docs are examples of the schema. When only some fields are selected, the
// (trimmed) examples alone don't show the full shape, so one complete document is included too.
function buildAddPrompt(docs, userInstruction, selectedFields, allFields) {
  const isPartial = selectedFields.length < allFields.length;
  const examples = pickDocFields(docs, selectedFields).map(d => _.omit(d, MANAGED_FIELDS));
  const parts = [ADD_PROMPT_HEADER];
  if (isPartial) {
    const fullExample = _.omit(docs[0], MANAGED_FIELDS);
    parts.push(
      `EXISTING DOCUMENTS (only the fields ${selectedFields.length ? `[${selectedFields.join(', ')}]` : '(none)'} are shown):`,
      JSON.stringify(examples, null, 2),
      'FULL DOCUMENT EXAMPLE (one existing document with ALL its fields, showing the complete shape a new document must have):',
      JSON.stringify(fullExample, null, 2),
    );
  } else {
    parts.push(
      'EXISTING DOCUMENTS (complete, all fields shown):',
      JSON.stringify(examples, null, 2),
    );
  }
  parts.push('USER INSTRUCTION:', userInstruction, ADD_PROMPT_FORMAT);
  return parts.join('\n\n');
}

function buildPrompt(addMode, docs, userInstruction, selectedFields, allFields) {
  return addMode
    ? buildAddPrompt(docs, userInstruction, selectedFields, allFields)
    : buildEditPrompt(docs, userInstruction, selectedFields);
}

function sanitizeNewDoc(doc) {
  return _.omitBy(doc, (v, k) => MANAGED_FIELDS.includes(k) || k.startsWith('__'));
}

function parseLLMJson(text) {
  if (!text) return null;
  const cleaned = text.replace(/```json\s*/gi, '').replace(/```\s*$/g, '').trim();
  const start = cleaned.indexOf('[');
  const end = cleaned.lastIndexOf(']');
  const slice = (start !== -1 && end !== -1) ? cleaned.slice(start, end + 1) : cleaned;
  return JSON.parse(slice);
}

function splitUpdate(update) {
  const first = {};
  const second = {};
  if (!_.isEmpty(update?.$set)) first.$set = update.$set;
  if (!_.isEmpty(update?.$unset)) first.$unset = update.$unset;
  if (!_.isEmpty(update?.$pull)) first.$pull = update.$pull;
  if (!_.isEmpty(update?.$addToSet)) second.$addToSet = update.$addToSet;
  if (!_.isEmpty(update?.$push)) second.$push = update.$push;
  return { first, second };
}

// onAddDocs(newDocs): add mode's Apply. Must add the docs to the page as unsaved (nothing is persisted here).
// Defaults to the CRUDPage's importJSON, which marks them __new, prepends and selects them.
export default function ModalBatchAIEditDocs({ docs, service, onCancel, onSave, onAddDocs, labelFields, model: defaultModel = 'gemini-flash-latest' }) {
  const { page } = useContext(LegoAdminPageContext);
  const isMounted = useMounted();

  const [userPrompt, setUserPrompt] = useState('');
  const [model, setModel] = useState(defaultModel);
  const [webSearch, setWebSearch] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rawText, setRawText] = useState('');
  const [updatesById, setUpdatesById] = useState(null);
  const [newDocs, setNewDocs] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [stats, setStats] = useState(null);
  const [activePromptId, setActivePromptId] = useState(null);
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const activePromptIdRef = useRef(null);
  const promptAreaRef = useRef(null);

  const availableFields = useMemo(
    () => _.without(_.uniq(_.flatMap(docs, d => Object.keys(d || {}))), '_id').sort(),
    [docs]
  );
  const [selectedFields, setSelectedFields] = useState(availableFields);

  const toggleField = (f) => setSelectedFields(
    sel => sel.includes(f) ? sel.filter(x => x !== f) : [...sel, f]
  );

  const fullPrompt = useMemo(
    () => buildPrompt(addMode, docs, userPrompt || '<USER INSTRUCTION GOES HERE>', selectedFields, availableFields),
    [addMode, docs, userPrompt, selectedFields, availableFields]
  );

  const switchMode = (isAdd) => {
    setAddMode(isAdd);
    setUpdatesById(null);
    setNewDocs(null);
    setParseError(null);
    setRawText('');
  };
  const wordCount = useMemo(() => (fullPrompt.match(/\S+/g) || []).length, [fullPrompt]);

  useEffect(() => {
    if (!isStreamingModel(model)) return;
    const myId = activePromptIdRef.current;
    let buf = '';
    const onProgress = ({ promptId, newOutput, stats: progressStats }) => {
      if (myId !== promptId || !isMounted()) return;
      if (progressStats) { setStats(progressStats); return; }
      buf += newOutput;
      setRawText(buf);
    };
    if (myId) page.service(STREAMING_SERVICE).on('prompt-progress', onProgress);
    return () => {
      if (myId) page.service(STREAMING_SERVICE).removeListener('prompt-progress', onProgress);
    };
  }, [activePromptId, model, isMounted, page]);

  const askAI = useCallback(async () => {
    if (!userPrompt.trim() || loading) return;
    setLoading(true);
    setRawText('');
    setUpdatesById(null);
    setNewDocs(null);
    setParseError(null);
    setStats(null);

    const prompt = buildPrompt(addMode, docs, userPrompt, selectedFields, availableFields);
    promptAreaRef.current?.save(userPrompt);

    try {
      let finalText;
      if (isStreamingModel(model)) {
        const promptId = Math.ceil(Math.random() * 1e6);
        activePromptIdRef.current = promptId;
        setActivePromptId(promptId);
        const res = await page.service(STREAMING_SERVICE).create({ prompt, promptId, model, webSearch });
        finalText = typeof res === 'string' ? res : (res?.message || JSON.stringify(res));
      } else {
        activePromptIdRef.current = null;
        setActivePromptId(null);
        const res = await page.service(FALLBACK_SERVICE).create({ userPrompt: prompt, options: { model, webSearch } });
        finalText = res?.message || (typeof res === 'string' ? res : '');
      }
      if (!isMounted()) return;
      setRawText(finalText);

      try {
        const parsed = parseLLMJson(finalText);
        if (!_.isArray(parsed)) throw new Error('Response is not a JSON array');
        if (addMode) {
          const cleanDocs = parsed.filter(d => _.isPlainObject(d)).map(sanitizeNewDoc).filter(d => !_.isEmpty(d));
          setNewDocs(cleanDocs);
        } else {
          const map = {};
          for (const entry of parsed) {
            if (entry && entry._id != null && entry.update) {
              map[String(entry._id)] = entry.update;
            }
          }
          setUpdatesById(map);
        }
      } catch (e) {
        setParseError(e.message);
      }
    } catch (err) {
      if (isMounted()) setParseError(`LLM request failed: ${err.message}`);
    } finally {
      if (isMounted()) {
        setLoading(false);
        activePromptIdRef.current = null;
        setActivePromptId(null);
      }
    }
  }, [userPrompt, docs, model, webSearch, page, isMounted, loading, selectedFields, addMode, availableFields]);

  // Add mode: hand the new docs to the page as unsaved objects. Saving is left to the user.
  const addNewDocs = useCallback(() => {
    if (!newDocs?.length) return;
    onAddDocs(newDocs.map(d => _.cloneDeep(d)));
    page.closeModal();
  }, [newDocs, onAddDocs, page]);
  const canAdd = _.isFunction(onAddDocs);

  const applyChanges = useCallback(() => {
    if (!updatesById || _.isEmpty(updatesById)) return;
    page.runAsync(async () => {
      const signature = page.getLoggedUserSignature();
      const splits = _.map(updatesById, (update, id) => ({ id, ...splitUpdate(update) }));

      await Promise.all(splits.map(({ id, first }) => {
        if (_.isEmpty(first)) return null;
        first.$set = { ...(first.$set || {}), updatedBy: signature };
        return service.update(id, first);
      }));

      await Promise.all(splits.map(({ id, second }) => {
        if (_.isEmpty(second)) return null;
        second.$set = { ...(second.$set || {}), updatedBy: signature };
        return service.update(id, second);
      }));

      page.selectNone();
      page.closeModal();
      onSave && onSave();
    });
  }, [updatesById, service, page, onSave]);

  const affectedCount = updatesById ? _.size(updatesById) : 0;
  const newDocsCount = newDocs ? newDocs.length : 0;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  return <div style={{ minWidth: '900px', minHeight: '50vh' }}>
    <div className={'mb-2'}>
      <div className={'d-flex align-items-center mb-1'}>
        <div className={'small text-muted flex-grow-1'}>
          {addMode
            ? <>{plural(docs.length, 'document')} selected as examples of the schema. Describe the new documents the LLM should create:</>
            : <>{plural(docs.length, 'document')} selected. Describe the edits you want the LLM to apply:</>}
        </div>
        {canAdd ? <label className={'small mb-0 ml-3'} style={{ cursor: 'pointer' }}
          title={'Instead of editing the selected docs, ask the LLM for NEW documents shaped like them. They are added to the list as unsaved, nothing is written to the DB until you save.'}>
          <input type={'checkbox'} className={'mr-1'} checked={addMode} onChange={(e) => switchMode(e.target.checked)} disabled={loading}/>
          <strong>Add new docs mode</strong>
        </label> : null}
      </div>
      <TextAreaWithHistory
        ref={promptAreaRef}
        id={addMode ? 'ModalBatchAIEditDocs-add' : 'ModalBatchAIEditDocs'}
        rows={4}
        className={'form-control form-control-sm'}
        value={userPrompt}
        onChange={setUserPrompt}
        placeholder={addMode
          ? 'e.g. Create the equivalent documents for the Toyota Corolla 2015-2019 and the Honda Civic 2016-2021.'
          : 'e.g. Set state to "approved" on all docs whose locales include "es-AR".'}
        disabled={loading}
      />
      <div className={'d-flex align-items-center mt-2'}>
        <button className={'btn btn-primary btn-sm'} onClick={askAI} disabled={loading || !userPrompt.trim()}>
          {loading
            ? <><span className={'spinner-border spinner-border-sm mr-1'} role={'status'} /> Asking AI...</>
            : (addMode ? 'Ask AI for new docs' : 'Ask AI for edits')}
        </button>
        <div className={'ml-2'} style={{ width: '280px' }}>
          <AIModelSelector value={model} onChange={setModel} />
        </div>
        <label className={'ml-3 small mb-0'} style={{ cursor: 'pointer' }} title={'Allow the model to use web search while answering'}>
          <input type={'checkbox'} className={'mr-1'} checked={webSearch} onChange={(e) => setWebSearch(e.target.checked)} disabled={loading}/>
          Do websearch
        </label>
        <div className={'ml-2'}>
          <AIResponseStats stats={stats} />
        </div>
      </div>

      <div className={'mt-1 small text-muted'}>
        <button className={'btn btn-link btn-sm p-0'} onClick={() => setShowPromptPreview(s => !s)}>
          {showPromptPreview ? 'Hide' : 'Prompt preview & fields'}
        </button>
        <span className={'ml-2'} title={'Approximate token count (word count proxy)'}>
          ~{wordCount.toLocaleString()} words
        </span>
        <span className={'ml-2'}>
          {selectedFields.length === availableFields.length
            ? `all ${availableFields.length} fields`
            : `${selectedFields.length} of ${availableFields.length} fields`}
        </span>
      </div>

      {showPromptPreview ? <div className={'border rounded p-2 mt-1 bg-light'}>
        <div className={'mb-2 small'}>
          <strong>Fields to send (besides <code>_id</code>):</strong>
          <button className={'btn btn-link btn-sm py-0 ml-2'}
            onClick={() => setSelectedFields(availableFields)}>All</button>
          <button className={'btn btn-link btn-sm py-0'}
            onClick={() => setSelectedFields([])}>None</button>
        </div>
        <div className={'d-flex flex-wrap mb-2'}>
          {availableFields.map(f => <label key={f} className={'mr-3 small mb-1'} style={{ cursor: 'pointer' }}>
            <input type={'checkbox'} className={'mr-1'}
              checked={selectedFields.includes(f)}
              onChange={() => toggleField(f)}/>
            <code>{f}</code>
          </label>)}
        </div>
        <div className={'small text-muted mb-1'}>
          Prompt preview (~{wordCount.toLocaleString()} words):
        </div>
        <pre className={'bg-white border rounded p-2 small mb-0'}
          style={{ whiteSpace: 'pre-wrap', maxHeight: '40vh', overflowY: 'auto' }}>{fullPrompt}</pre>
      </div> : null}
    </div>

    {parseError ? <div className={'alert alert-danger py-1 px-2 small'}>
      <strong>Could not parse LLM response:</strong> {parseError}
    </div> : null}

    {updatesById !== null && !parseError ? <div className={'mt-3'}>
      <div className={'mb-2'}>
        <span className={'badge badge-primary mr-1'}>{affectedCount}</span>
        of <span className={'badge badge-secondary mr-1'}>{docs.length}</span> docs would change.
      </div>
      <div style={{ maxHeight: '50vh', overflowY: 'auto' }} className={'border rounded'}>
        <table className={'table table-sm mb-0'}>
          <thead className={'bg-light'}>
            <tr>
              <th style={{ width: '180px' }}>Document</th>
              <th>Update</th>
              <th>Diff preview</th>
            </tr>
          </thead>
          <tbody>
          {docs.map(doc => {
            const upd = updatesById[String(doc._id)];
            if (!upd) return null;
            const after = applyMongoUpdate(doc, upd);
            const changed = diffKeys(doc, after);
            return <tr key={doc._id}>
              <td><DocIdentity doc={doc} labelFields={labelFields} /></td>
              <td><pre className={'small mb-0'} style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(upd, null, 2)}</pre></td>
              <td>
                {changed.map(k => <div key={k} className={'small mb-1'}>
                  <div><code>{k}</code></div>
                  <TextDiff type='json' inputA={doc[k]} ellipsis={200} inputB={after[k]} />
                </div>)}
              </td>
            </tr>;
          })}
          </tbody>
        </table>
      </div>
    </div> : null}

    {newDocs !== null && !parseError ? <div className={'mt-3'}>
      <div className={'mb-2'}>
        <span className={'badge badge-success mr-1'}>{newDocsCount}</span>
        new {plural(newDocsCount, 'document').replace(/^\d+ /, '')} proposed.
        {newDocsCount ? <span className={'text-muted small ml-2'}>
          They will be added to the list as <strong>unsaved</strong>; review and save them yourself.
        </span> : null}
      </div>
      {newDocsCount ? <div style={{ maxHeight: '50vh', overflowY: 'auto' }} className={'border rounded'}>
        <table className={'table table-sm mb-0'}>
          <thead className={'bg-light'}>
            <tr>
              <th style={{ width: '180px' }}>New document</th>
              <th>Content</th>
            </tr>
          </thead>
          <tbody>
          {newDocs.map((doc, i) => <tr key={i}>
            <td>
              <span className={'badge badge-success'}>new #{i + 1}</span>
              {pickIdentityFields(doc, labelFields).map(f => <div key={f.key}
                title={`${f.key}: ${f.value}`}
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '170px' }}
                className={'small'}>
                {f.key}&nbsp;<code className={'monospace text-secondary select-text'}>{f.value}</code>
              </div>)}
            </td>
            <td><pre className={'small mb-0'} style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(doc, null, 2)}</pre></td>
          </tr>)}
          </tbody>
        </table>
      </div> : null}
    </div> : null}

    {rawText && updatesById === null && newDocs === null && !loading ? <div className={'mt-2'}>
      <div className={'small text-muted'}>Raw LLM response:</div>
      <pre className={'bg-light p-2 rounded small'} style={{ whiteSpace: 'pre-wrap', maxHeight: '30vh', overflowY: 'auto' }}>{rawText}</pre>
    </div> : null}

    <div className={'mt-3'}>
      {addMode
        ? <button
          className={'btn btn-success'}
          disabled={newDocsCount === 0 || loading}
          title={'Adds the docs to the list as unsaved. Nothing is written until you save.'}
          onClick={addNewDocs}>
          Add {plural(newDocsCount, 'doc')} to the list (unsaved)
        </button>
        : <button
          className={'btn btn-success'}
          disabled={!updatesById || affectedCount === 0 || loading}
          onClick={applyChanges}>
          Apply {plural(affectedCount, 'change')}
        </button>}
      <button className={'btn btn-light ml-2'} onClick={onCancel}>Cancel</button>
    </div>
  </div>;
}
